import {
    ReactElement,
    useRef,
} from 'react';

import {
    Alert,
    Button,
    Group,
    SimpleGrid,
    Text,
} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useRegistrars} from '../hooks/useRegistrars';
import {useRegistrarsSearch} from '../hooks/useRegistrarsSearch';
import {useRegistrarsSelection} from '../hooks/useRegistrarsSelection';
import {useRegistrarsTableColumns} from '../hooks/useRegistrarsTableColumns';
import {Registrar} from '../types';
import {useAuth} from '../../../hooks/useAuth.ts';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions.ts';

// FNP-1292 — the registrars table, on the SHARED component.
//
// ⚠️ WHAT THE HAND-ROLLED `<Table>` MADE IMPOSSIBLE, not merely absent: sorting
// (no `sortStatus` contract to wire), token pagination (`SimpleTablePagination`
// is offset-shaped and the service discarded `nextToken`), and the distinct
// empty and error states (`TableBody` renders one blank body for all three).
// Those four boxes close as a consequence of this one change; they were never
// four independent defects.
export const RecordsTable = ({
                                 extraTools,
                             }: {
    extraTools?: ReactElement[],
}) => {

    const {allowedActions} = useVerifiedPermissions();

    const {user} = useAuth();

    if (!user) {
        throw new Error('user is required parameter');
    }

    const cognitoUsername = user.username;

    const {columns} = useRegistrarsTableColumns();

    const {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        isReloading,
        nextToken,
        fetchNextPage,
        fetchRecords,
    } = useRegistrars();

    const {
        keyword,
        handleFilterByKeyword,
    } = useRegistrarsSearch();

    const {
        selectedRecords,
        setSelectedRecords,
    } = useRegistrarsSelection();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const searching = keyword.trim() !== '';

    const canDelete = allowedActions.includes('DeleteRegistrar');

    // ⚠️ THE TWO EMPTY STATES MEAN OPPOSITE THINGS and `noRecordsText` is a
    // single string, so the distinction is branched here. A filtered empty
    // result is not "you have no registrars" — telling an operator that sends
    // them hunting for a bug that is not there.
    const noRecordsText = searching
        ? I18n.get('No loaded registrar matches this search')
        : I18n.get('No registrars yet');

    // ⚠️ ROW SELECTION IS GATED ON OWNERSHIP, and that predates this migration.
    //
    // `listRegistrars` scopes to the caller's own records for a non-privileged
    // caller, but a privileged caller sees everyone's — and only the creator
    // may delete. The old table rendered a DISABLED checkbox on someone else's
    // row; `mantine-datatable` expresses the same rule as `isRecordSelectable`,
    // which greys the box for exactly those rows.
    const isRecordSelectable = (record: Registrar) =>
        record.createdByUsername === cognitoUsername;

    // The creator column only exists for a caller who can delete — it is there
    // to explain why some rows are not selectable, so without the checkboxes it
    // has nothing to explain.
    const tableColumns = canDelete
        ? [
            ...columns,
            {
                accessor: 'createdByUsername',
                sortable: true,
                title: I18n.get('Created By'),
                render: (item: Registrar) => (
                    <Text size={'sm'}>{item.createdBy?.username ?? item.createdByUsername ?? '—'}</Text>
                ),
            },
        ]
        : columns;

    return (
        <SimpleGrid verticalSpacing={'xl'} m={'sm'}>

            <Group justify={'space-between'} align={'center'}>
                <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>
                {extraTools}
            </Group>

            {/* ⚠️ DISTINCT FROM AN EMPTY TABLE: this is the QUERY failing, so
                nothing at all is known — not "you have no registrars". It is
                reachable only because RegistrarsService re-throws now; while it
                swallowed the error and returned undefined, a failed fetch
                rendered as an empty list. */}
            {isError && (
                <Alert color={'red'} title={I18n.get('Could not load registrars')}>
                    <Group justify={'space-between'} align={'center'}>
                        {I18n.get('The domains API did not return your registrars. The request itself failed.')}
                        <Button
                            variant={'outline'}
                            size={'xs'}
                            onClick={fetchRecords}
                            loading={isReloading}
                        >
                            {I18n.get('Retry')}
                        </Button>
                    </Group>
                </Alert>
            )}

            <MantineDataTable<Registrar>
                columns={tableColumns}
                records={visibleItems}
                idAccessor={'id'}
                selectedRecords={canDelete ? selectedRecords : undefined}
                onSelectedRecordsChange={canDelete ? setSelectedRecords : undefined}
                isRecordSelectable={canDelete ? isRecordSelectable : undefined}
                // Client-side sorting over the pages loaded so far — the same
                // scope the keyword works on. Token pagination cannot reorder
                // rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                // ⚠️ THE LOADING STATE, distinct from empty and from error.
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={fetchNextPage}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                // Passing a term is what selects the filtered footer state —
                // omit it and a narrowed view silently reports the unfiltered
                // one.
                filterTerm={searching ? keyword : undefined}
                noun={'registrar'}
                nextToken={nextToken}
                isFetchingMore={isFetchingNextPage}
                handleLoadMore={fetchNextPage}
            />

        </SimpleGrid>
    );
};
